import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { getTrialStatus } from "../utils/trialStatus";
import {
  CALCULATOR_CATEGORIES,
  CALCULATOR_TOOLS,
  getCalculatorCategory,
} from "../data/calculatorCatalog";
import { FORMULA_REFERENCE_DATABASE } from "../data/formulaReferenceDatabase";
import {
  MATERIAL_DATABASE,
  MATERIAL_GROUPS,
} from "../data/machiningMaterials";
import usePwaInstall from "../hooks/usePwaInstall";
import {
  calculateWorkspaceScore,
  loadWorkspacePreferences,
  normalizeRecentTools,
  saveWorkspacePreferences,
} from "../utils/workspacePreferences";
import { getShopStats } from "../utils/shopDataStore";
import {
  getCollaborativeProjectsForUser,
  getPendingProjectInvites,
} from "../utils/collaborativeProjectService";
import "./Dashboard.css";

const Dashboard = ({ user, preferences }) => {
  const location = useLocation();
  const initialCategory = new URLSearchParams(location.search).get("category") || "all";
  const [trialStatus, setTrialStatus] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState("unknown");
  const [accessAllowed, setAccessAllowed] = useState(true);
  const [founderProfile, setFounderProfile] = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(true);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [calculatorSearch, setCalculatorSearch] = useState("");
  const [sortMode, setSortMode] = useState("grouped");
  const [viewMode, setViewMode] = useState("grid");
  const [workspacePrefs, setWorkspacePrefs] = useState(() =>
    loadWorkspacePreferences(user?.uid)
  );
  const [pinnedCollapsed, setPinnedCollapsed] = useState(false);
  const [healthCheckResults, setHealthCheckResults] = useState([]);
  const pwaInstall = usePwaInstall();
  const [threeSProjectStats, setThreeSProjectStats] = useState({
    total: 0,
    open: 0,
    complete: 0,
    completion: 0,
    upcoming: [],
  });
  const [threeSProjectError, setThreeSProjectError] = useState("");
  const [collaborationStats, setCollaborationStats] = useState({
    projects: 0,
    pendingInvites: 0,
    recent: [],
  });
  const calculatorsSectionRef = useRef(null);
  const shopStats = useMemo(() => getShopStats(), []);

  useEffect(() => {
    setActiveCategory(initialCategory);
  }, [initialCategory]);

  useEffect(() => {
    setWorkspacePrefs(loadWorkspacePreferences(user?.uid));
  }, [user?.uid]);

  useEffect(() => {
    const refreshPreferences = () => setWorkspacePrefs(loadWorkspacePreferences(user?.uid));
    window.addEventListener("manucalc:workspace-preferences-updated", refreshPreferences);
    return () =>
      window.removeEventListener("manucalc:workspace-preferences-updated", refreshPreferences);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    let mounted = true;

    const loadThreeSProjects = async () => {
      try {
        setThreeSProjectError("");
        const snapshot = await getDocs(collection(db, "users", user.uid, "threeSProjects"));
        const projects = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        const complete = projects.filter((project) => project.status === "Complete" || project.currentStage === "Complete").length;
        const open = projects.length - complete;
        const upcoming = projects
          .filter((project) => project.dueDate && project.status !== "Complete" && project.currentStage !== "Complete")
          .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))
          .slice(0, 3);

        if (mounted) {
          setThreeSProjectStats({
            total: projects.length,
            open,
            complete,
            completion: projects.length ? Math.round((complete / projects.length) * 100) : 0,
            upcoming,
          });
        }
      } catch (error) {
        if (error?.code === "permission-denied") {
          console.warn("Dashboard 3S projects are not readable for this signed-in user. Check Firestore rules for users/{uid}/threeSProjects.", error);
          if (mounted) setThreeSProjectError("3S projects need the updated Firestore rules deployed.");
        } else {
          console.warn("Dashboard 3S project summary could not be loaded.", error);
          if (mounted) setThreeSProjectError("3S project summary could not be loaded. Check connection and try again.");
        }
      }
    };

    loadThreeSProjects();
    return () => {
      mounted = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    let mounted = true;
    Promise.all([
      getCollaborativeProjectsForUser(user),
      getPendingProjectInvites(user),
    ])
      .then(([projects, invites]) => {
        if (!mounted) return;
        setCollaborationStats({
          projects: projects.length,
          pendingInvites: invites.length,
          recent: projects.slice(0, 3),
        });
      })
      .catch((error) => {
        console.warn("Collaborative project summary could not be loaded.", error);
      });
    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const fetchTrialStatus = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          return;
        }

        const data = userDoc.data();

        if (data?.preferences?.hideWalkthrough === true) {
          setShowWalkthrough(false);
        }

        const status = getTrialStatus(data.trialStartDate, data.trialEndDate);
        const billingStatus = data.subscriptionStatus || "unknown";
        const hasActiveSubscription =
          billingStatus === "active" || billingStatus === "trialing";
        const isCompanySeat =
          data.accountBillingType === "companySeat" ||
          data.billingExempt === true ||
          data.subscriptionRequired === false;
        const isFounder = data.isFounder === true;

        setSubscriptionStatus(billingStatus);
        setFounderProfile(isFounder ? {
          founderNumber: data.founderNumber || null,
          founderGrantedAt: data.founderGrantedAt || null,
        } : null);
        setTrialStatus(status);
        setAccessAllowed(
          hasActiveSubscription || status?.isTrialActive || isCompanySeat || isFounder
        );
      } catch (error) {
        console.error("Error fetching trial:", error);
      }
    };

    fetchTrialStatus();

    const interval = setInterval(fetchTrialStatus, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const hideWalkthrough = async () => {
    setShowWalkthrough(false);

    try {
      const userDocRef = doc(db, "users", user.uid);
      await setDoc(
        userDocRef,
        { preferences: { hideWalkthrough: true } },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to save walkthrough preference:", err);
    }
  };

  const filteredTools = useMemo(() => {
    const normalizedSearch = calculatorSearch.trim().toLowerCase();
    const tools = CALCULATOR_TOOLS.filter((tool) => {
      const matchesCategory =
        activeCategory === "all" || tool.categories.includes(activeCategory);
      const hidden =
        activeCategory === "all" &&
        tool.categories.some((category) => workspacePrefs.hiddenCategories?.includes(category));
      const matchesSearch =
        !normalizedSearch ||
        [tool.name, tool.description, ...(tool.categories || []), ...(tool.tags || [])]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch);

      return matchesCategory && !hidden && matchesSearch;
    });

    if (sortMode === "az") {
      return tools.slice().sort((a, b) => a.name.localeCompare(b.name));
    }

    if (sortMode === "category") {
      return tools
        .slice()
        .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
    }

    return tools.slice().sort((a, b) => {
      const aCategory = getCalculatorCategory(a.category)?.name || a.category;
      const bCategory = getCalculatorCategory(b.category)?.name || b.category;
      return aCategory.localeCompare(bCategory) || a.name.localeCompare(b.name);
    });
  }, [activeCategory, calculatorSearch, sortMode, workspacePrefs.hiddenCategories]);

  const categoryCounts = useMemo(() => {
    return CALCULATOR_CATEGORIES.filter(
      (category) => !workspacePrefs.hiddenCategories?.includes(category.id)
    ).map((category) => ({
      ...category,
      count: CALCULATOR_TOOLS.filter((tool) => tool.categories.includes(category.id)).length,
    }));
  }, [workspacePrefs.hiddenCategories]);

  const hiddenCategories = useMemo(
    () => CALCULATOR_CATEGORIES.filter((category) => workspacePrefs.hiddenCategories?.includes(category.id)),
    [workspacePrefs.hiddenCategories]
  );

  const favoriteTools = useMemo(
    () => (workspacePrefs.favorites || [])
      .map((id) => CALCULATOR_TOOLS.find((tool) => tool.id === id))
      .filter(Boolean),
    [workspacePrefs.favorites]
  );

  const recentTools = useMemo(
    () => normalizeRecentTools(workspacePrefs.recentTools || [])
      .map((activity) => {
        const calculator = CALCULATOR_TOOLS.find((tool) => tool.id === activity.toolId || tool.id === activity.id);
        return {
          ...activity,
          id: activity.id || activity.toolId,
          name: calculator?.name || activity.name,
          badge: calculator?.badge || "OS",
          description: calculator?.description || activity.type || "Workspace tool",
          route: activity.route || (calculator ? `/calculator/${calculator.id}` : "#"),
          category: calculator?.category || activity.type || "Workspace",
        };
      }),
    [workspacePrefs.recentTools]
  );

  const universalResults = useMemo(() => {
    const normalized = calculatorSearch.trim().toLowerCase();
    if (!normalized) return { formulas: [], materials: [], categories: [] };
    const formulas = FORMULA_REFERENCE_DATABASE.filter((formula) =>
      [
        formula.name,
        formula.category,
        formula.formula,
        formula.example,
        formula.notes,
        ...Object.keys(formula.variables || {}),
        ...Object.values(formula.variables || {}),
        ...(formula.tags || []),
        ...(formula.relatedCalculators || []),
      ].join(" ").toLowerCase().includes(normalized)
    ).slice(0, 6);
    const materials = Object.entries(MATERIAL_DATABASE)
      .map(([id, material]) => ({ id, ...material }))
      .filter((material) =>
        [
          material.label,
          MATERIAL_GROUPS[material.group],
          material.group,
          String(material.sfm),
          String(material.hardnessHB),
        ].join(" ").toLowerCase().includes(normalized)
      ).slice(0, 6);
    const categories = CALCULATOR_CATEGORIES.filter((category) =>
      [category.name, category.description, category.id].join(" ").toLowerCase().includes(normalized)
    );
    return { formulas, materials, categories };
  }, [calculatorSearch]);

  const workspaceScore = useMemo(
    () => calculateWorkspaceScore(workspacePrefs, categoryCounts.length, CALCULATOR_CATEGORIES.length),
    [workspacePrefs, categoryCounts.length]
  );

  const displayName = user?.displayName || user?.email || "Machinist";
  const dashboardThemeClass = "dashboard-dark";
  const formatRecentTime = (value) => {
    if (!value) return "recently";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "recently";
    return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const scrollToCalculators = () => {
    window.setTimeout(() => {
      const target = calculatorsSectionRef.current;
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.scrollY - 18;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, 0);
  };

  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
    scrollToCalculators();
  };

  const persistWorkspacePrefs = (next) => {
    const saved = saveWorkspacePreferences(user?.uid, next);
    setWorkspacePrefs(saved);
  };

  const toggleFavorite = (toolId) => {
    const favorites = workspacePrefs.favorites || [];
    persistWorkspacePrefs({
      ...workspacePrefs,
      favorites: favorites.includes(toolId)
        ? favorites.filter((id) => id !== toolId)
        : [toolId, ...favorites].slice(0, 24),
    });
  };

  const moveFavorite = (toolId, direction) => {
    const favorites = [...(workspacePrefs.favorites || [])];
    const index = favorites.indexOf(toolId);
    if (index < 0) return;

    const nextIndex = direction === "up" ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= favorites.length) return;

    const [item] = favorites.splice(index, 1);
    favorites.splice(nextIndex, 0, item);
    persistWorkspacePrefs({
      ...workspacePrefs,
      favorites,
    });
  };

  const hideCategory = (categoryId) => {
    persistWorkspacePrefs({
      ...workspacePrefs,
      hiddenCategories: Array.from(new Set([...(workspacePrefs.hiddenCategories || []), categoryId])),
    });
  };

  const restoreCategory = (categoryId) => {
    persistWorkspacePrefs({
      ...workspacePrefs,
      hiddenCategories: (workspacePrefs.hiddenCategories || []).filter((id) => id !== categoryId),
    });
  };

  const toggleMode = () => {
    persistWorkspacePrefs({
      ...workspacePrefs,
      mode: workspacePrefs.mode === "beginner" ? "advanced" : "beginner",
    });
  };

  const completeWorkflow = (workflowId) => {
    persistWorkspacePrefs({
      ...workspacePrefs,
      completedWorkflows: Array.from(new Set([...(workspacePrefs.completedWorkflows || []), workflowId])),
    });
  };

  const runHealthCheck = () => {
    const checks = [
      {
        level: (workspacePrefs.favorites || []).length ? "green" : "yellow",
        text: (workspacePrefs.favorites || []).length ? "Pinned tools configured" : "No pinned calculators yet",
      },
      {
        level: (workspacePrefs.hiddenCategories || []).length ? "green" : "yellow",
        text: (workspacePrefs.hiddenCategories || []).length ? "Unused categories have been sorted out" : "All categories visible; hide unused fields to reduce clutter",
      },
      {
        level: (workspacePrefs.recentTools || []).length >= 3 ? "green" : "yellow",
        text: (workspacePrefs.recentTools || []).length >= 3 ? "Recent-tool trail is active" : "Open a few calculators to build a recent-tool trail",
      },
      {
        level: (workspacePrefs.completedWorkflows || []).length ? "green" : "yellow",
        text: (workspacePrefs.completedWorkflows || []).length ? "At least one workflow reviewed" : "No connected workflow has been reviewed yet",
      },
      {
        level: workspaceScore.overallScore >= 70 ? "green" : workspaceScore.overallScore >= 45 ? "yellow" : "red",
        text: `3S workspace score is ${workspaceScore.overallScore}`,
      },
    ];
    setHealthCheckResults(checks);
    persistWorkspacePrefs({
      ...workspacePrefs,
      healthChecks: (workspacePrefs.healthChecks || 0) + 1,
    });
  };

  if (!accessAllowed) {
    return (
      <div className={`dashboard premium-dashboard ${dashboardThemeClass}`}>
        <section className="dashboard-hero">
          <span className="dashboard-kicker">Trial Expired</span>
          <h1>Restore access to ManuCalc.</h1>
          <p>Your 1-hour free trial has ended.</p>
          <Link to="/auth" className="premium-primary-link">
            Continue to Billing
          </Link>
        </section>
      </div>
    );
  }

  const showTrialBanner = Boolean(trialStatus?.isTrialActive);
  const displayTime = showTrialBanner
    ? trialStatus.totalHoursRemaining >= 24
      ? trialStatus.formattedTimeRemaining
      : trialStatus.shortFormattedTimeRemaining
    : "";

  return (
    <div className={`dashboard premium-dashboard ${dashboardThemeClass}`}>
      {showTrialBanner && (
        <div className={`trial-banner ${trialStatus.status}`}>
          <strong>{displayTime}</strong>{" "}
          {trialStatus?.totalHoursRemaining >= 24
            ? "remaining in your billing trial"
            : "until billing redirect"}
        </div>
      )}
      {founderProfile && (
        <div className="trial-banner founder-banner">
          <strong>Founding Member{founderProfile.founderNumber ? ` #${founderProfile.founderNumber}` : ""}</strong>{" "}
          Lifetime ManuCalc access is active.
        </div>
      )}

      <section className="dashboard-hero">
        <div>
          <span className="dashboard-kicker">Welcome back, {displayName}</span>
          <h1>Manufacturing Calculators</h1>
          <p>
            Accurate tools for machining, fabrication, welding, inspection,
            production planning, quoting, and shop-floor manufacturing math.
          </p>
          <div className="dashboard-hero-actions">
            <Link to="/calculator/gdt" className="premium-primary-link">
              Open GD&T Helper
            </Link>
            <Link to="/jobs" className="premium-secondary-link">
              Manufacturing Plans
            </Link>
          </div>
        </div>
        <div className="dashboard-hero-metrics">
          <div>
            <span>Calculators</span>
            <strong>{CALCULATOR_TOOLS.length}</strong>
          </div>
          <div>
            <span>Categories</span>
            <strong>{CALCULATOR_CATEGORIES.length}</strong>
          </div>
          <div>
            <span>Coverage</span>
            <strong>Shopwide</strong>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-heading">
          <div>
            <span className="dashboard-kicker">Shop KPI Dashboard</span>
            <h2>Current manufacturing workload</h2>
          </div>
          <Link to="/shop-floor" className="premium-secondary-link">Open Shop Floor Mode</Link>
        </div>
        <div className="three-s-grid">
          {[
            ["Active Plans", shopStats.activePlans, "/jobs"],
            ["Open Travelers", shopStats.openTravelers, "/job-travelers"],
            ["Setup Sheets", shopStats.setupSheets, "/setup-sheets"],
            ["Inspection Sheets", shopStats.inspectionSheets, "/inspection"],
            ["Machines Loaded", shopStats.machinesLoaded, "/machines"],
            ["Estimated Shop Hours", shopStats.estimatedShopHours, "/capacity-planner"],
            ["Tools", shopStats.tools, "/tools"],
            ["Shop Brain Notes", shopStats.shopBrainNotes, "/shop-brain"],
          ].map(([label, value, to]) => (
            <Link key={label} to={to} className="three-s-score-card" style={{ textDecoration: "none" }}>
              <span>{label}</span>
              <strong>{value}</strong>
              <p>{Number(value) === 0 ? "No records yet. Open this workspace to add one." : "Open workspace"}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-heading">
          <div>
            <span className="dashboard-kicker">Collaboration</span>
            <h2>My collaborative projects</h2>
            <p>Individual project spaces for machinists, customers, programmers, fabricators, and outside partners.</p>
          </div>
          <Link to="/collaborative-projects" className="premium-primary-link">Open Collaborative Projects</Link>
        </div>
        <div className="three-s-grid">
          <div className="three-s-score-card">
            <span>Projects</span>
            <strong>{collaborationStats.projects}</strong>
            <p>Owned or shared with you.</p>
          </div>
          <div className="three-s-score-card">
            <span>Pending Invites</span>
            <strong>{collaborationStats.pendingInvites}</strong>
            <p>Invites matching your login email.</p>
          </div>
          <div className="three-s-score-card overall">
            <span>Recent</span>
            <strong>{collaborationStats.recent[0]?.name || "None"}</strong>
            <p>{collaborationStats.recent.length ? "Open the workspace for details." : "Create a project to start collaborating."}</p>
          </div>
        </div>
      </section>

      {healthCheckResults.length > 0 && (
        <section className="dashboard-section health-check-panel">
          <div>
            <span className="dashboard-kicker">Shine</span>
            <h2>Job / Workspace Health Check</h2>
          </div>
          <div className="health-check-grid">
            {healthCheckResults.map((check) => (
              <div key={check.text} className={`health-check-card ${check.level}`}>
                <strong>{check.level === "green" ? "OK" : check.level === "yellow" ? "Review" : "Fix"}</strong>
                <span>{check.text}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {showWalkthrough && (
        <section className="dashboard-help-panel">
          <div>
            <h3>Control Center Workflow</h3>
            <p>
              Use the sidebar, category cards, search, or filters to jump into
              the right manufacturing calculator. Save outputs to a job/customer
              plan from each calculator page.
            </p>
          </div>
          <button className="btn btn-secondary" type="button" onClick={hideWalkthrough}>
            Hide Walkthrough
          </button>
        </section>
      )}

      <section className="dashboard-section three-s-panel">
        <div className="section-heading">
          <div>
            <span className="dashboard-kicker">3S Workspace</span>
            <h2>Workplace improvement projects</h2>
          </div>
          <div className="dashboard-inline-actions">
            <button type="button" className="premium-secondary-link" onClick={toggleMode}>
              {workspacePrefs.mode === "beginner" ? "Beginner Mode" : "Advanced Mode"}
            </button>
            <Link to="/3s" className="premium-primary-link">
              Open 3S Workspace
            </Link>
          </div>
        </div>
        <div className="three-s-grid">
          <div className="three-s-score-card">
            <span>Total Projects</span>
            <strong>{threeSProjectStats.total}</strong>
            <p>Documented workplace improvement projects.</p>
          </div>
          <div className="three-s-score-card">
            <span>Open</span>
            <strong>{threeSProjectStats.open}</strong>
            <p>Not started, active, blocked, or waiting for review.</p>
          </div>
          <div className="three-s-score-card">
            <span>Complete</span>
            <strong>{threeSProjectStats.completion}%</strong>
            <p>{threeSProjectStats.complete} completed projects.</p>
          </div>
          <div className="three-s-score-card overall">
            <span>Workspace Score</span>
            <strong>{workspaceScore.overallScore}</strong>
            <button type="button" onClick={runHealthCheck}>Run Job Health Check</button>
          </div>
        </div>
        {threeSProjectStats.upcoming.length > 0 && (
          <div className="three-s-upcoming-strip">
            <strong>Upcoming 3S due dates</strong>
            {threeSProjectStats.upcoming.map((project) => (
              <Link key={project.id} to="/3s">
                {project.projectName} | {project.dueDate}
              </Link>
            ))}
          </div>
        )}
        {threeSProjectError && (
          <div className="three-s-upcoming-strip warning">
            <strong>3S project status</strong>
            <span>{threeSProjectError}</span>
          </div>
        )}
      </section>

      <section className="dashboard-section install-panel">
        <div className="install-copy">
          <span className="dashboard-kicker">Download ManuCalc</span>
          <h2>Mobile and desktop app access</h2>
          <p>
            Install ManuCalc as a real app shortcut on this device. Installed mode still requires sign-in and still follows trial/subscription access.
          </p>
          <div className="install-status-grid">
            <span>{pwaInstall.isInstalled ? "Installed mode detected" : "Browser mode"}</span>
            <span>{pwaInstall.serviceWorkerReady ? "Offline shell ready" : "Service worker warming up"}</span>
            <span>{pwaInstall.canInstall ? "Native install prompt available" : `${pwaInstall.browserName} guided install`}</span>
          </div>
        </div>
        <div className="install-action-card">
          <button type="button" className="premium-primary-link download-app-button" onClick={pwaInstall.startInstall}>
            Download Mobile/Desktop App
          </button>
          {pwaInstall.installResult && <p className="install-result-message">{pwaInstall.installResult}</p>}
          {(pwaInstall.guideOpen || !pwaInstall.canInstall || pwaInstall.deviceType === "ios") && (
            <div className="install-guide-panel">
              <div>
                <strong>{pwaInstall.instructions.title}</strong>
                <p>{pwaInstall.instructions.limitation}</p>
              </div>
              <ol>
                {pwaInstall.instructions.steps.map((step) => <li key={step}>{step}</li>)}
              </ol>
            </div>
          )}
        </div>
      </section>

      <section className="dashboard-section pinned-calculators-section">
        <div className="section-heading">
          <div>
            <span className="dashboard-kicker">My Pinned Calculators</span>
            <h2>Fast access tools</h2>
            <p>
              Pin calculators from the cards below and reorder them here. Pinned
              calculators are stored with your ManuCalc workspace on this device.
            </p>
          </div>
          <button
            type="button"
            className="premium-secondary-link"
            onClick={() => setPinnedCollapsed((current) => !current)}
          >
            {pinnedCollapsed ? "Show Pinned" : "Collapse"}
          </button>
        </div>

        {!pinnedCollapsed && (
          <div className="pinned-calculator-grid">
            {favoriteTools.length ? favoriteTools.map((tool, index) => {
              const category = getCalculatorCategory(tool.category);
              return (
                <article key={tool.id} className={`pinned-calculator-card accent-${category.accent}`}>
                  <Link to={`/calculator/${tool.id}`} className="pinned-calculator-main">
                    <span className="premium-card-icon">{tool.badge}</span>
                    <div>
                      <small>Pinned • {category.name}</small>
                      <strong>{tool.name}</strong>
                      <p>{tool.description}</p>
                    </div>
                  </Link>
                  <div className="pinned-calculator-actions">
                    <button type="button" onClick={() => moveFavorite(tool.id, "up")} disabled={index === 0}>
                      Move Up
                    </button>
                    <button type="button" onClick={() => moveFavorite(tool.id, "down")} disabled={index === favoriteTools.length - 1}>
                      Move Down
                    </button>
                    <button type="button" onClick={() => toggleFavorite(tool.id)}>
                      Unpin
                    </button>
                  </div>
                </article>
              );
            }) : (
              <div className="pinned-empty-state">
                <strong>No pinned calculators yet</strong>
                <p>Use the Pin button on any calculator card to build your quick-access tool set.</p>
                <button type="button" className="premium-secondary-link" onClick={scrollToCalculators}>
                  Browse Calculators
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {recentTools.length > 0 && (
        <section className="dashboard-section quick-access-section">
          <div className="quick-access-column">
            <span className="dashboard-kicker">Recently Used</span>
            <h2>Last used workspace tools</h2>
            <div className="compact-tool-list">
              {recentTools.slice(0, 8).map((tool) => (
                <Link key={tool.id} to={tool.route || `/calculator/${tool.toolId}`}>
                  <span>{tool.badge} {tool.name}</span>
                  <small>{formatRecentTime(tool.lastUsedAt)} | Used {tool.usageCount || 1}x</small>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="dashboard-section workflow-section">
        <div className="section-heading">
          <div>
            <span className="dashboard-kicker">Workflow Mode</span>
            <h2>Connected manufacturing paths</h2>
          </div>
        </div>
        <div className="workflow-grid">
          {[
            ["machining-flow", "Material to Quote", ["material-cost", "feeds-speeds", "tool-deflection", "mrr", "machine-time", "part-cost-estimate"]],
            ["threading-flow", "Threading Workflow", ["tap-drill", "thread-percentage", "thread-engagement", "thread-cut-depth", "threading"]],
            ["layout-flow", "Bolt Pattern / Layout", ["bolt-pattern", "arc-center", "chamfer-mill", "trig", "circle-calculator"]],
            ["sheet-flow", "Sheet Metal Bend", ["bend-allowance", "bend-deduction", "flat-pattern", "k-factor", "press-brake-tonnage"]],
            ["weld-flow", "Welding Estimate", ["weld-volume", "weld-filler-estimate", "weld-heat-input", "weld-cost"]],
            ["inspection-flow", "Inspection / Tolerance", ["gdt", "true-position", "mmc-bonus", "go-nogo-limits", "tolerance-stack"]],
          ].map(([id, title, steps]) => (
            <article key={id} className="workflow-card">
              <strong>{title}</strong>
              <div>
                {steps.map((step) => {
                  const tool = CALCULATOR_TOOLS.find((item) => item.id === step);
                  return tool ? <Link key={step} to={`/calculator/${step}`}>{tool.name}</Link> : null;
                })}
              </div>
              <button type="button" onClick={() => completeWorkflow(id)}>
                {workspacePrefs.completedWorkflows?.includes(id) ? "Workflow reviewed" : "Mark reviewed"}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-section custom-tool-request-panel compact-request-panel">
        <div className="section-heading">
          <div>
            <span className="dashboard-kicker">Custom Tool Request</span>
            <h2>Ask for the next calculator or shop tool</h2>
            <p>
              Send a detailed request directly to the ManuCalc platform owner.
              Company administrators cannot approve or modify platform tools.
            </p>
          </div>
          <Link className="premium-primary-link" to="/request-custom-tool">Request Custom Calculator</Link>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-heading">
          <div>
            <span className="dashboard-kicker">Browse by Category</span>
            <h2>Manufacturing fields</h2>
          </div>
          {hiddenCategories.length > 0 && (
            <div className="hidden-category-strip">
              <span>Hidden fields:</span>
              {hiddenCategories.map((category) => (
                <button key={category.id} type="button" onClick={() => restoreCategory(category.id)}>
                  Restore {category.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="category-card-grid">
          {categoryCounts.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`premium-category-card accent-${category.accent} ${
                activeCategory === category.id ? "active" : ""
              }`}
              onClick={() => selectCategory(category.id)}
            >
              <span className="premium-card-icon">{category.icon}</span>
              <strong>{category.name}</strong>
              <small>{category.count} calculators</small>
              <p>{category.description}</p>
              <span
                role="button"
                tabIndex={0}
                className="category-hide-action"
                onClick={(event) => {
                  event.stopPropagation();
                  hideCategory(category.id);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.stopPropagation();
                    hideCategory(category.id);
                  }
                }}
              >
                Hide field
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="dashboard-section calculators-anchor-section" ref={calculatorsSectionRef} id="calculator-library">
        <div className="section-heading tools-heading">
          <div>
            <span className="dashboard-kicker">All Calculators</span>
            <h2>Search, filter, and launch</h2>
          </div>
          <div className="calculator-controls">
            <input
              className="calculator-search"
              type="search"
              placeholder="Search NPT, GD&T, bend, tap drill..."
              value={calculatorSearch}
              onChange={(event) => setCalculatorSearch(event.target.value)}
            />
            <select
              value={activeCategory}
              onChange={(event) => selectCategory(event.target.value)}
            >
              <option value="all">All categories</option>
              {CALCULATOR_CATEGORIES.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value)}>
              <option value="grouped">Grouped by field</option>
              <option value="az">A-Z</option>
              <option value="category">Category</option>
            </select>
            <div className="view-toggle">
              <button
                type="button"
                className={viewMode === "grid" ? "active" : ""}
                onClick={() => setViewMode("grid")}
              >
                Grid
              </button>
              <button
                type="button"
                className={viewMode === "list" ? "active" : ""}
                onClick={() => setViewMode("list")}
              >
                List
              </button>
            </div>
          </div>
        </div>

        <div className="calculator-library-summary">
          Showing {filteredTools.length} of {CALCULATOR_TOOLS.length} calculators
          {activeCategory !== "all" ? ` in ${getCalculatorCategory(activeCategory)?.name || activeCategory}` : ""}.
        </div>

        {calculatorSearch.trim() && (
          <div className="universal-search-panel">
            <div>
              <strong>Formula hits</strong>
              {universalResults.formulas.length ? universalResults.formulas.map((formula) => (
                <Link key={formula.name} to={`/formula-reference?search=${encodeURIComponent(formula.name)}`}>{formula.name}</Link>
              )) : <span>No formula matches</span>}
            </div>
            <div>
              <strong>Material hits</strong>
              {universalResults.materials.length ? universalResults.materials.map((material) => (
                <Link key={material.id} to="/material-data">{material.label}</Link>
              )) : <span>No material matches</span>}
            </div>
            <div>
              <strong>Category hits</strong>
              {universalResults.categories.length ? universalResults.categories.map((category) => (
                <button key={category.id} type="button" onClick={() => selectCategory(category.id)}>{category.name}</button>
              )) : <span>No category matches</span>}
            </div>
          </div>
        )}

        {filteredTools.length === 0 ? (
          <div className="calculator-empty-state">
            <strong>No calculators found</strong>
            <p>Try a different manufacturing field or search term.</p>
            <button
              type="button"
              className="premium-secondary-link"
              onClick={() => {
                setActiveCategory("all");
                setCalculatorSearch("");
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className={`tools-grid premium-tools-grid ${viewMode === "list" ? "list" : ""}`}>
            {filteredTools.map((tool) => {
              const category = getCalculatorCategory(tool.category);
              return (
                <Link
                  key={tool.id}
                  to={`/calculator/${tool.id}`}
                  className={`premium-tool-card accent-${category.accent}`}
                >
                  <span className="premium-card-icon">{tool.badge}</span>
                  <div>
                    <small>{category.name}</small>
                    <strong>{tool.name}</strong>
                    <p>{tool.description}</p>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={workspacePrefs.favorites?.includes(tool.id) ? `Unpin ${tool.name}` : `Pin ${tool.name}`}
                    title={workspacePrefs.favorites?.includes(tool.id) ? `Unpin ${tool.name}` : `Pin ${tool.name}`}
                    className={`open-pill pin-pill ${workspacePrefs.favorites?.includes(tool.id) ? "active" : ""}`}
                    onClick={(event) => {
                      event.preventDefault();
                      toggleFavorite(tool.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        toggleFavorite(tool.id);
                      }
                    }}
                  >
                    {workspacePrefs.favorites?.includes(tool.id) ? "Pinned" : "Pin"}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {subscriptionStatus === "past_due" && (
        <div className="trial-banner">
          Billing needs attention before the next renewal.
        </div>
      )}
    </div>
  );
};

export default Dashboard;
